import { type FC, useState } from 'react';
import Select from '../atoms/Select';
import Button from '../atoms/Button';
import FormField from './FormField';
import { type ProductCategory } from '../../types/products';

interface ProductFiltersProps {
  categories: ProductCategory[];
  healthGoals: ProductCategory[];
  onFiltersChange: (filters: {
    search?: string;
    categories?: string[];
    healthGoals?: string[];
    sortBy?: 'name' | 'category';
    sortOrder?: 'asc' | 'desc';
  }) => void;
  initialFilters?: {
    search?: string;
    categories?: string[];
    healthGoals?: string[];
    sortBy?: 'name' | 'category';
    sortOrder?: 'asc' | 'desc';
  };
}

export const ProductFilters: FC<ProductFiltersProps> = ({
  categories,
  healthGoals,
  onFiltersChange,
  initialFilters = {}
}) => {
  const [search, setSearch] = useState(initialFilters.search ?? '');
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    initialFilters.categories ?? []
  );
  const [selectedHealthGoals, setSelectedHealthGoals] = useState<string[]>(
    initialFilters.healthGoals ?? []
  );
  const [sortBy, setSortBy] = useState<'name' | 'category'>(
    initialFilters.sortBy ?? 'name'
  );
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
    initialFilters.sortOrder ?? 'asc'
  );

  const handleApplyFilters = () => {
    onFiltersChange({
      search: search || undefined,
      categories: selectedCategories.length > 0 ? selectedCategories : undefined,
      healthGoals: selectedHealthGoals.length > 0 ? selectedHealthGoals : undefined,
      sortBy,
      sortOrder
    });
  };

  const handleReset = () => {
    setSearch('');
    setSelectedCategories([]);
    setSelectedHealthGoals([]);
    setSortBy('name');
    setSortOrder('asc');
    onFiltersChange({});
  };

  const handleCategoryToggle = (categoryId: string) => {
    setSelectedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleHealthGoalToggle = (goalId: string) => {
    setSelectedHealthGoals(prev =>
      prev.includes(goalId)
        ? prev.filter(id => id !== goalId)
        : [...prev, goalId]
    );
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters</h3>
      
      <div className="space-y-4">
        <FormField
          id="search"
          label="Search Products"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or description..."
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Categories
          </label>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {categories.map((category) => (
              <label
                key={category.id}
                className="flex items-center space-x-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedCategories.includes(category.id)}
                  onChange={() => handleCategoryToggle(category.id)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{category.name}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Health Goals
          </label>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {healthGoals.map((goal) => (
              <label
                key={goal.id}
                className="flex items-center space-x-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedHealthGoals.includes(goal.id)}
                  onChange={() => handleHealthGoalToggle(goal.id)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{goal.name}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sort By
            </label>
            <Select
              value={sortBy}
              onChange={(value) => setSortBy(value as 'name' | 'category')}
              options={[
                { value: 'name', label: 'Name' },
                { value: 'category', label: 'Category' }
              ]}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Order
            </label>
            <Select
              value={sortOrder}
              onChange={(value) => setSortOrder(value as 'asc' | 'desc')}
              options={[
                { value: 'asc', label: 'Ascending' },
                { value: 'desc', label: 'Descending' }
              ]}
            />
          </div>
        </div>

        <div className="flex space-x-2 pt-4">
          <Button onClick={handleApplyFilters} className="flex-1">
            Apply Filters
          </Button>
          <Button onClick={handleReset} variant="secondary" className="flex-1">
            Reset
          </Button>
        </div>
      </div>
    </div>
  );
};